/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import PropTypes from 'prop-types'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useTagManager from '../../hooks/use-tag-manager'

/**
 * Higher-order component that modifies the given component's `onClick` to show the login form if
 * the user is not logged in.
 * @param {React.Component} component Component to wrap.
 * @param {(location: Location, locale: string) => boolean} [options.isLoginPage] Function that
 * determines whether the current page is the "login" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/login".
 * @returns {React.Component} wrapped component
 */
const withRegistration = (
    Component,
    {isLoginPage = (loc) => loc.pathname.endsWith('/login')} = {}
) => {
    const WrappedComponent = ({onClick = noop, isCart = false, ...passThroughProps}) => {
        const {
            category,
            productid,
            productname,
            wishlistitems,
            isplp,
            ispdp,
            isCartItem,
            isEmptyCart = false,
            ...DOMProps
        } = passThroughProps
        const {data: customer} = useCurrentCustomer()
        const authModal = useAuthModal()
        const showToast = useToast()
        const location = useLocation()
        const tagManager = useTagManager()
        const {formatMessage, locale} = useIntl()

        const handleClick = (e) => {
            e.preventDefault()
            if (isEmptyCart) {
                tagManager.ctaClick(
                    'cta_click',
                    'Cart Interaction',
                    'cta click - Sign In',
                    'Empty Cart - Sign In',
                    'Sign In'
                )
            }
            if (!customer.isRegistered) {
                if (isplp || ispdp) {
                    // TODO: If in future requirement comes to apply this on all tiles, remove this check.
                    tagManager.wishlistInteraction(
                        category,
                        productid,
                        productname,
                        wishlistitems,
                        'login'
                    )
                }
                // Do not show auth modal if users is already on the login page
                if (isLoginPage(location, locale)) {
                    showToast({
                        title: formatMessage({
                            defaultMessage: 'Please sign in to continue!',
                            id: 'with_registration.info.please_sign_in'
                        }),
                        status: 'info'
                    })
                } else {
                    authModal.onOpen()
                    if (isCart) {
                        tagManager.ctaClick(
                            'cta_click',
                            'wishlist',
                            'sign in',
                            'cart wishlist messaging',
                            'Sign in'
                        )
                    }
                }
            } else {
                onClick(e)
            }
        }

        return (
            <React.Fragment>
                <Component
                    {...DOMProps}
                    onClick={handleClick}
                    className="with-registration-component wishlist-button"
                />
                {authModal.isOpen && (
                    <AuthModal
                        {...authModal}
                        onLoginSuccess={onClick}
                        isCartItem={isCartItem}
                        productid={productid}
                    />
                )}
            </React.Fragment>
        )
    }
    WrappedComponent.propTypes = {
        onClick: PropTypes.func,
        isCart: PropTypes.bool
    }
    return WrappedComponent
}

export default withRegistration
